/// <amd-module name="Tfs/Frame/Components/Help/UserSettings" />
import * as React from "react";
import { IUserSettingsMenu } from "Tfs/Frame/Components/Help/UserSettingsService";
import * as Resources from "Tfs/Frame/Resources";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { IContributedCommand, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { ExpandableContainer, IExpandable } from "VSSUI/Expandable";
import { ContextualMenu, IMenuItem, MenuItemType } from "VSSUI/Menu";
import { Location } from "VSSUI/Utilities/Position";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import { MenuIcon } from "./MenuIcon";
import { Button } from "VSSUI/Button";

const userSetingsHubRoute = "ms.vss-admin-web.user-admin-hub-route";

export class UserSettings extends VssComponent<{}> {
    private telemetryService: IVssTelemetryService;
    private service = this.context.pageContext.getService<IUserSettingsMenu>("IUserSettingsMenu");
    private expandableContainerRef: React.RefObject<ExpandableContainer> = React.createRef();

    public render(): JSX.Element {
        return (
            <ScreenSizeConditional condition={(screenSize) => screenSize > ScreenSize.xsmall}>
                <ExpandableContainer
                    ref={this.expandableContainerRef}
                    excludeFocusZone={true}
                    renderCallout={this.renderCallout}
                    role="menuitem"
                >
                    <Button
                        ariaLabel={Resources.UserSettings}
                        className="icon-only"
                        subtle={true}
                        tooltipProps={{ text: Resources.UserSettings }}
                    >
                        <MenuIcon
                            onComponentRendered={this.setUserSettingsMenu}
                            iconName={"PlayerSettings"}
                        />
                    </Button>
                </ExpandableContainer>
            </ScreenSizeConditional>
        );
    }

    public setUserSettingsMenu = (): void => {
        if (this.expandableContainerRef && this.expandableContainerRef.current) {
            this.service.setUserSettingsMenu(this.expandableContainerRef.current.expand);
        }
    };

    public renderCallout = (dropdown: IExpandable, id: string, element: HTMLElement): JSX.Element => {
        return (
            <ContextualMenu
                onDismiss={dropdown.collapse}
                anchorElement={element}
                anchorOrigin={{ horizontal: Location.end, vertical: Location.end }}
                menuProps={{
                    id: "user-settings-menu",
                    items: this.getMenuItems()
                }}
                menuOrigin={{ horizontal: Location.end, vertical: Location.start }}
            />
        );
    };

    private getMenuItems(): IMenuItem[] {
        const locationService = this.context.pageContext.getService<IVssLocationService>("IVssLocationService");
        let url = locationService.routeUrl(userSetingsHubRoute, { adminPivot: "about" });
        const menuItems: IMenuItem[] = [];

        menuItems.push({
            text: Resources.PreviewFeatures,
            rank: 10,
            id: "previewFeatureLink",
            onActivate: this._showPreviewFeatures,
            iconProps: {
                iconName: "Preview",
                className: "admin-tab-icon"
            }
        });

        menuItems.push({ id: "separator", itemType: MenuItemType.Divider, rank: 20 });

        menuItems.push({
            text: Resources.Profile,
            rank: 30,
            id: "profilePageLink",
            href: url,
            iconProps: {
                iconName: "ContactInfo",
                className: "admin-tab-icon"
            }
        });

        url = locationService.routeUrl(userSetingsHubRoute, { adminPivot: "preferences" });
        menuItems.push({
            text: Resources.Preferences,
            rank: 40,
            id: "preferencesPageLink",
            href: url,
            iconProps: {
                iconName: "WorldClock",
                className: "admin-tab-icon"
            }
        });

        const showRefreshPermissions = isFeatureFlagEnabled(
            this.context.pageContext,
            "VisualStudio.Services.AdminEngagement.ProfileSettingsView.EnableRefreshPermissionsTab",
            false
        );

        if (showRefreshPermissions) {
            url = locationService.routeUrl(userSetingsHubRoute, { adminPivot: "permissionsRefresh" });
            menuItems.push({
                text: Resources.PermissionsRefresh,
                rank: 45,
                id: "permissionsRefreshPageLink",
                href: url,
                iconProps: {
                    iconName: "Refresh",
                    className: "admin-tab-icon"
                }
            });
        }

        menuItems.push({ id: "separator", itemType: MenuItemType.Divider, rank: 50 });

        url = locationService.routeUrl(userSetingsHubRoute, { adminPivot: "notifications" });
        menuItems.push({
            id: "notifications",
            href: url,
            rank: 60,
            text: Resources.Notifications,
            iconProps: {
                iconName: "CannedChat",
                className: "admin-tab-icon"
            }
        });

        menuItems.push({
            text: Resources.Theme,
            rank: 70,
            id: "changeThemeLink",
            onActivate: this._showThemePanel,
            iconProps: {
                iconName: "Color",
                className: "admin-tab-icon"
            }
        });

        url = locationService.routeUrl(userSetingsHubRoute, { adminPivot: "usage" });
        menuItems.push({
            id: "usage",
            href: url,
            rank: 80,
            text: Resources.Usage,
            iconProps: {
                iconName: "ChartSeries",
                className: "admin-tab-icon"
            }
        });

        menuItems.push({ id: "separator", itemType: MenuItemType.Divider, rank: 90 });

        url = locationService.routeUrl(userSetingsHubRoute, { adminPivot: "tokens" });
        menuItems.push({
            id: "patPage",
            href: url,
            rank: 100,
            text: Resources.PATPage,
            iconProps: {
                iconName: "Signin",
                className: "admin-tab-icon"
            }
        });

        url = locationService.routeUrl(userSetingsHubRoute, { adminPivot: "keys" });
        menuItems.push({
            id: "sshkeyPage",
            href: url,
            rank: 110,
            text: Resources.SSHPublicKeys,
            iconProps: {
                iconName: "TwoKeys",
                className: "admin-tab-icon"
            }
        });

        return menuItems;
    }

    private _showPreviewFeatures = () => {
        this._initializeTelemetry();
        this.telemetryService.publishEvent("UserSettingsMenu", "PreviewFeature", { Action: "Show Preview Features" });
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const openPreviewFeatruePanel: IContributedCommand = {
            methodName: "show",
            serviceName: "IFeaturePanel",
            dependencies: ["ms.vss-tfs-web.feature-management-content"]
        };
        contributionService.executeCommandEx("preview-feature", openPreviewFeatruePanel);
    };

    private _showThemePanel = () => {
        this._initializeTelemetry();
        this.telemetryService.publishEvent("UserSettingsMenu", "ChangeTheme", { Action: "Show Theme Panel" });
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const openChangeThemePanel: IContributedCommand = {
            methodName: "show",
            serviceName: "IThemePanelService",
            dependencies: ["ms.vss-tfs-web.theme-management-content", "ms.vss-tfs-web.available-themes-data-provider"]
        };
        contributionService.executeCommandEx("change-theme", openChangeThemePanel);
    };

    private _initializeTelemetry() {
        if (!this.telemetryService) {
            this.telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        }
    }
}

VssComponent.register("ms.vss-tfs-web.user-settings", UserSettings);
